import pRetry from 'p-retry';
import { withTimeout } from '../withTimeout/withTimeout';

export type LoadFailedAttemptInfo = {
  attemptNumber: number;
  error: Error;
  retriesLeft: number;
};

export type LoadSuccessInfo = {
  attemptNumber: number;
};

export type LoadWithRetryOptions<T> = {
  load: (attemptNumber: number) => Promise<T>;
  onFailedAttempt?: (info: LoadFailedAttemptInfo) => void;
  onSuccess?: (info: LoadSuccessInfo) => void;
  retryDelayMs: number;
  signal?: AbortSignal;
  timeoutMs: number;
  totalAttempts: number;
};

export const loadWithRetry = <T>({
  load,
  onFailedAttempt,
  onSuccess,
  retryDelayMs,
  signal,
  timeoutMs,
  totalAttempts,
}: LoadWithRetryOptions<T>): Promise<T> => {
  // eslint-disable-next-line fp/no-let
  let lastAttempt = 0;
  return pRetry(
    (attemptNumber) => {
      // eslint-disable-next-line fp/no-mutation
      lastAttempt = attemptNumber;
      return withTimeout(load(attemptNumber), timeoutMs);
    },
    {
      factor: 1,
      maxTimeout: retryDelayMs,
      minTimeout: retryDelayMs,
      onFailedAttempt: (error) =>
        onFailedAttempt?.({
          attemptNumber: error.attemptNumber,
          error,
          retriesLeft: error.retriesLeft,
        }),
      randomize: false,
      retries: Math.max(0, totalAttempts - 1),
      signal,
    },
  ).then((value) => {
    if (!signal?.aborted) {
      onSuccess?.({ attemptNumber: lastAttempt });
    }
    return value;
  });
};
